import { useMutation } from "@apollo/client";
import {
  RefreshPastilleDocument,
  UpdateManyOrderDocument,
} from "@jasper/shared";
import {
  OrderStatus,
  SyncThreeshapeOrdersDocument,
} from "../../../generated/graphql";
import { notifications } from "@mantine/notifications";
import { SelectableOrder } from "./useOrderSelection";

interface Props {
  onStatusesUpdated: () => void;
  onOrdersSynced?: () => void;
}

export const useOrderActions = ({
  onStatusesUpdated,
  onOrdersSynced,
}: Props) => {
  const [updateOrderStatus] = useMutation(UpdateManyOrderDocument);
  const [refreshPastille] = useMutation(RefreshPastilleDocument);
  const [syncOrdersFrom3ShapeMutation] = useMutation(
    SyncThreeshapeOrdersDocument
  );

  const updateOrdersStatus = async (
    status: OrderStatus,
    orders: SelectableOrder[]
  ) => {
    try {
      const orderIds = orders.map(order => order.id);

      await updateOrderStatus({
        variables: {
          where: {
            id: {
              in: orderIds,
            },
          },
          data: {
            status: {
              set: status,
            },
          },
        },
      });
      onStatusesUpdated();

      // For refreshPastille, we only update for the current page orders when all are selected
      // since we don't have all order objects with their previous status
      await refreshPastille({
        variables: {
          args: orders.map(order => ({
            userGroupId: order.providerGroupId,
            previousUserGroupId: order.providerGroupId,
            orderId: order.id,
            previousStatus: order.status,
            currentStatus: status,
          })),
        },
      });

      const count = orders.length;
      notifications.show({
        title: `${count} orders status updated successfully`,
        color: "green",
        message: "",
      });
    } catch (error) {
      console.error(error);
      notifications.show({
        title: "Error updating orders status",
        color: "red",
        message: "",
      });
    }
  };

  const syncOrdersFrom3Shape = async ({
    fromDate,
    toDate,
  }: {
    fromDate: Date;
    toDate: Date;
  }) => {
    try {
      notifications.show({
        title: "Syncing orders from 3Shape...",
        id: "syncing-orders-3shape",
        color: "blue",
        message: "",
        loading: true,
        autoClose: false,
      });
      await syncOrdersFrom3ShapeMutation({
        variables: {
          fromDate,
          toDate,
        },
      });
      notifications.hide("syncing-orders-3shape");
      notifications.show({
        title: "Orders synced successfully from 3Shape",
        color: "green",
        message: "",
      });
      onOrdersSynced?.();
    } catch (err) {
      notifications.hide("syncing-orders-3shape");

      console.error(err);
      notifications.show({
        title: "Error syncing orders from 3Shape",
        color: "red",
        message: "",
      });
    }
  };

  return {
    updateOrdersStatus,
    syncOrdersFrom3Shape,
  };
};
